/**
 * Assinatura eletronica por provedor (P-MIN-4): configuracao da empresa, pedidos ao
 * provedor, log e versao assinada. Ver docs/ADR/0062-assinatura-por-provedor.md.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';

import { api, requestPage } from '../client';

export type SignatureProviderKey = 'nenhum' | 'falso' | 'clicksign';
/**
 * encerrado_incompleto: o provedor encerrou o envelope com signatario sem assinar. Nao e
 * assinatura concluida (ADR 0079, R5-C01).
 */
export type SignatureRequestStatus =
  | 'pendente'
  | 'assinado'
  | 'recusado'
  | 'expirado'
  | 'cancelado'
  | 'erro'
  | 'encerrado_incompleto';
export type SignatureRequestSignerRole =
  | 'seguradora'
  | 'tomador'
  | 'segurado'
  | 'corretor'
  | 'representante_legal'
  | 'avalista'
  | 'testemunha';
export type SignatureRequestMovement =
  'emissao' | 'endosso' | 'cancelamento' | 'renovacao';

export interface SignatureProviderInfo {
  key: Exclude<SignatureProviderKey, 'nenhum'>;
  name: string;
  /** simulado (provedor falso), sandbox ou producao. */
  environment: 'simulado' | 'sandbox' | 'producao';
  polling_allowed: boolean;
}

export interface SignatureSettings {
  provider: SignatureProviderKey;
  /** O provedor escolhido esta ligado nesta instalacao (a credencial fica no servidor). */
  provider_ready: boolean;
  default_signer_name: string;
  default_signer_email: string;
  /** CPF com so o final; vazio quando nao ha. */
  default_signer_document_masked: string;
  default_signer_auth: 'email' | 'automatica';
  deadline_days: number;
  auto_movements: SignatureRequestMovement[];
  /** O inicio mais antigo entre os movimentos ligados. */
  auto_since: string | null;
  /** Desde quando cada movimento ligado pede assinatura sozinho. */
  auto_since_by_movement: Partial<Record<SignatureRequestMovement, string>>;
  saved: boolean;
  changed_label: string;
  updated_at: string | null;
  providers: SignatureProviderInfo[];
}

export interface SignatureSettingsInput {
  provider: SignatureProviderKey;
  default_signer_name: string;
  default_signer_email: string;
  /** Ausente mantem o CPF gravado; vazio apaga. */
  default_signer_document?: string;
  default_signer_auth: 'email' | 'automatica';
  deadline_days: number;
  auto_movements: SignatureRequestMovement[];
}

export interface SignatureRequestSigner {
  id: string;
  position: number;
  name: string;
  email: string;
  document_masked: string;
  role: SignatureRequestSignerRole;
  auth: 'email' | 'automatica';
  status: 'pendente' | 'assinado' | 'recusado';
  decided_at: string | null;
}

export interface SignatureRequest {
  id: string;
  number: number;
  owner_type: string;
  owner_id: string;
  generated_document_id: string | null;
  policy_id: string | null;
  subject_label: string;
  provider: Exclude<SignatureProviderKey, 'nenhum'>;
  provider_name: string;
  trigger: 'manual' | 'automatico';
  status: SignatureRequestStatus;
  message: string;
  deadline_at: string;
  /** O prazo passou com o pedido aberto. */
  overdue: boolean;
  source: {
    filename: string;
    content_type: string;
    byte_size: number;
    checksum: string;
  };
  external_envelope_id: string;
  external_document_id: string;
  failure_code: string;
  failure_message: string;
  /** Versao assinada, guardada ao lado da original. */
  signed: {
    filename: string;
    byte_size: number;
    checksum: string;
    signed_at: string;
  } | null;
  requested_label: string;
  sent_at: string | null;
  finished_at: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
  signers: SignatureRequestSigner[];
}

export interface SignatureRequestEvent {
  id: string;
  kind: string;
  source: 'usuario' | 'webhook' | 'consulta' | 'sistema';
  signer_id: string | null;
  provider_event: string;
  message: string;
  detail: Record<string, unknown>;
  actor_label: string;
  occurred_at: string;
  created_at: string;
}

export interface SignatureRequestSignerInput {
  name: string;
  email: string;
  document?: string;
  role: SignatureRequestSignerRole;
}

export interface SignatureRequestInput {
  signers: SignatureRequestSignerInput[];
  include_default_signer: boolean;
  message: string;
  deadline_days?: number;
}

export interface SignatureRequestListParams extends ListParams {
  provider?: string;
  policy_id?: string;
}

export const signatureSettings = {
  get: (signal?: AbortSignal) =>
    api.get<SignatureSettings>('/api/v1/signature-settings', signal),
  update: (input: SignatureSettingsInput) =>
    api.put<SignatureSettings>('/api/v1/signature-settings', input),
};

export const signatureRequests = {
  /** Lista com a contagem por situacao do recorte em `counts`. */
  list: async (params: SignatureRequestListParams, signal?: AbortSignal) => {
    const page = await requestPage<SignatureRequest>(
      `/api/v1/signature-requests${query(params)}`,
      signal,
    );
    const counts = (page.meta.counts as Record<string, number> | undefined) ?? {};
    return { ...page, counts };
  },
  get: (id: string, signal?: AbortSignal) =>
    api.get<SignatureRequest>(`/api/v1/signature-requests/${id}`, signal),
  events: (id: string, signal?: AbortSignal) =>
    api.get<SignatureRequestEvent[]>(`/api/v1/signature-requests/${id}/events`, signal),
  signedFile: (id: string) =>
    api.get<{ url: string; filename: string; format: 'pdf' }>(
      `/api/v1/signature-requests/${id}/signed-file`,
    ),
  cancel: (id: string, reason: string) =>
    api.post<SignatureRequest>(`/api/v1/signature-requests/${id}/cancellation`, {
      reason,
    }),
  remind: (id: string, message = '') =>
    api.post<SignatureRequest>(`/api/v1/signature-requests/${id}/reminder`, {
      message,
    }),
  sync: (id: string) =>
    api.post<SignatureRequest>(`/api/v1/signature-requests/${id}/sync`, {}),
  listByDocument: (documentId: string, signal?: AbortSignal) =>
    api.get<SignatureRequest[]>(
      `/api/v1/generated-documents/${documentId}/signature-requests`,
      signal,
    ),
  create: (documentId: string, input: SignatureRequestInput) =>
    api.post<SignatureRequest>(
      `/api/v1/generated-documents/${documentId}/signature-requests`,
      input,
    ),
};
